import { c as _c } from "react/compiler-runtime";
import { TableContainer, Table, TableRow, styled, TableCell, Divider, Stack, Typography } from '@mui/material';
import { BackupPolicyType, ScheduleMode, type BackupVault, type BackupPolicyConfig, Provider } from '@repo/api-gw-sdk';
import React from 'react';
import { AccountAsVaultTag, VaultTag } from '@/components/vaults/vaultTag';
import { ResourceTypes } from '@/data/inventory/resourceTypes';
import { fromDaysToBiggestAsPossible } from '@/utils/dateTime';
import { pluralize } from '@/utils/string';
import { deconstructCron, getFrequencyByWindow, getFrequencyDisplayName, getHoursDisplay, getWindowDurationDisplay } from '../frequency';
const PolicyTableCell = styled(TableCell)(() => ({
  borderBottom: 'none',
  padding: 0,
  verticalAlign: 'top',
  paddingRight: '40px',
  '&:last-child': {
    paddingRight: 0
  }
}));
const PolicyTableHeaderCell = styled(TableCell)(() => ({
  border: 'none',
  padding: 0,
  paddingBottom: '24px',
  paddingRight: '40px',
  '&:last-child': {
    paddingRight: 0
  }
}));
interface BackupPolicyRetentionSchedulesTableProps {
  policy: BackupPolicyConfig;
  vaults?: BackupVault[];
}
const BackupPolicySchedulesTableContainer = props => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <BackupPolicySchedulesTableHeader />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props.children) {
    t1 = <TableContainer className="mb-[12px]"><Table>{t0}<tbody>{props.children}</tbody></Table></TableContainer>;
    $[1] = props.children;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
export function BackupPolicySchedulesTable(props) {
  const $ = _c(21);
  const isNativeStandard = props.policy.policyType === BackupPolicyType.BACKUP_POLICY_TYPE_AWS_NATIVE_STANDARD;
  const isAzureFiles = props.policy.policyType === BackupPolicyType.BACKUP_POLICY_TYPE_AZURE_FILES;
  const resourceType = props.policy.resourceTypes?.[0];
  const sourceCloudProvider = isAzureFiles ? Provider.Azure : resourceType ? ResourceTypes[resourceType].provider : Provider.Aws;
  let t0;
  if ($[0] !== props.policy.scheduleMode || $[1] !== props.policy.schedules[0]?.useUtc) {
    t0 = props.policy.scheduleMode === ScheduleMode.SCHEDULE_MODE_ADVANCED && <Typography className="mb-[36px] mt-[12px]">{props.policy.schedules[0]?.useUtc ? "Backup schedules are in UTC." : "Backup schedules follow the resource time zones."}</Typography>;
    $[0] = props.policy.scheduleMode;
    $[1] = props.policy.schedules[0]?.useUtc;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== isAzureFiles || $[4] !== isNativeStandard || $[5] !== props.policy.scheduleMode || $[6] !== props.policy.schedules || $[7] !== props.vaults || $[8] !== sourceCloudProvider) {
    let t2;
    if ($[10] !== isAzureFiles || $[11] !== isNativeStandard || $[12] !== props.policy.scheduleMode || $[13] !== props.vaults || $[14] !== sourceCloudProvider) {
      t2 = (schedule, index, items) => {
        const vault = props.vaults?.find(v => v.id === schedule.vaultId);
        const {
          hours,
          minutes
        } = deconstructCron(schedule.windows);
        const frequencyDisplayName = getFrequencyDisplayName(schedule.windows);
        return <React.Fragment key={schedule.id}><TableRow><PolicyTableCell width="272px"><Stack direction="row" sx={{
                gap: "12px"
              }}><Typography sx={{
                  width: "80px",
                  minWidth: "80px",
                  maxWidth: "80px"
                }}>{getFrequencyByWindow(schedule.windows)?.text}</Typography>{props.policy.scheduleMode === ScheduleMode.SCHEDULE_MODE_ADVANCED && <Stack sx={{
                  gap: "12px",
                  maxWidth: "140px"
                }}>{frequencyDisplayName && <Typography>{frequencyDisplayName}</Typography>}<Typography variant="subtitle2">{getHoursDisplay(hours, minutes)}<br />{`Starts within ${getWindowDurationDisplay(schedule.duration / 60)}`}</Typography></Stack>}</Stack></PolicyTableCell><PolicyTableCell width="140px"><RetentionCell retentionDays={schedule.backupRetention} /></PolicyTableCell><PolicyTableCell>{isNativeStandard || isAzureFiles ? <AccountAsVaultTag cloudProvider={sourceCloudProvider} region={schedule.region || "Source region"} /> : vault && <VaultTag vault={vault} />}</PolicyTableCell></TableRow>{index !== items.length - 1 && <TableRow><TableCell colSpan={3} sx={{
              border: "none",
              padding: "24px 0"
            }}><Divider sx={{
                borderStyle: "dashed"
              }} /></TableCell></TableRow>}</React.Fragment>;
      };
      $[10] = isAzureFiles;
      $[11] = isNativeStandard;
      $[12] = props.policy.scheduleMode;
      $[13] = props.vaults;
      $[14] = sourceCloudProvider;
      $[15] = t2;
    } else {
      t2 = $[15];
    }
    t1 = props.policy.schedules.map(t2);
    $[3] = isAzureFiles;
    $[4] = isNativeStandard;
    $[5] = props.policy.scheduleMode;
    $[6] = props.policy.schedules;
    $[7] = props.vaults;
    $[8] = sourceCloudProvider;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  let t2;
  if ($[16] !== t1) {
    t2 = <BackupPolicySchedulesTableContainer>{t1}</BackupPolicySchedulesTableContainer>;
    $[16] = t1;
    $[17] = t2;
  } else {
    t2 = $[17];
  }
  let t3;
  if ($[18] !== t0 || $[19] !== t2) {
    t3 = <Stack>{t0}{t2}</Stack>;
    $[18] = t0;
    $[19] = t2;
    $[20] = t3;
  } else {
    t3 = $[20];
  }
  return t3;
}
export const BackupPolicyContinuousSchedulesTable = t0 => {
  const $ = _c(11);
  const {
    policy
  } = t0;
  const resourceType = policy.resourceTypes?.[0];
  const cloudProvider = resourceType ? ResourceTypes[resourceType].provider : Provider.ProviderUnspecified;
  const isBigQueryPitr = !!policy.bigQueryNativePitrConfig;
  const retentionDays = policy.bigQueryNativePitrConfig?.retentionDays ?? policy.awsNativePitrConfig?.retentionDays ?? policy.azureSqlDatabaseNativePitrConfig?.retentionDays ?? 0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <PolicyTableCell width="272px"><Typography>Continuous</Typography></PolicyTableCell>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== isBigQueryPitr || $[2] !== retentionDays) {
    t2 = isBigQueryPitr ? "Based on time travel window" : `${retentionDays} ${pluralize("day", retentionDays)}`;
    $[1] = isBigQueryPitr;
    $[2] = retentionDays;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <PolicyTableCell width="140px"><Typography>{t2}</Typography></PolicyTableCell>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== cloudProvider) {
    t4 = <PolicyTableCell><AccountAsVaultTag cloudProvider={cloudProvider} region="Source-region" /></PolicyTableCell>;
    $[6] = cloudProvider;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t3 || $[9] !== t4) {
    t5 = <BackupPolicySchedulesTableContainer><TableRow>{t1}{t3}{t4}</TableRow></BackupPolicySchedulesTableContainer>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
};
const RetentionCell = t0 => {
  const $ = _c(11);
  const {
    retentionDays
  } = t0;
  let t1;
  if ($[0] !== retentionDays) {
    t1 = fromDaysToBiggestAsPossible(retentionDays);
    $[0] = retentionDays;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const formattedRetention = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      gap: "4px"
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = `${formattedRetention.value} ${formattedRetention.unit}`;
  let t4;
  if ($[3] !== t3) {
    t4 = <Typography>{t3}</Typography>;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== formattedRetention.unit || $[6] !== retentionDays) {
    t5 = formattedRetention.unit !== "days" && <Typography>{`(${retentionDays} ${pluralize("day", retentionDays)})`}</Typography>;
    $[5] = formattedRetention.unit;
    $[6] = retentionDays;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t4 || $[9] !== t5) {
    t6 = <Stack sx={t2}>{t4}{t5}</Stack>;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
};
const BackupPolicySchedulesTableHeader = () => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <PolicyTableHeaderCell width="272px"><Typography variant="h4">Frequency</Typography></PolicyTableHeaderCell>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <PolicyTableHeaderCell width="140px"><Typography variant="h4">Retention</Typography></PolicyTableHeaderCell>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <thead><TableRow>{t0}{t1}<PolicyTableHeaderCell><Typography variant="h4">Destination</Typography></PolicyTableHeaderCell></TableRow></thead>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};